import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Sparkle, ArrowClockwise, ArrowRight, Warning, Clock, CheckCircle, DotsThreeVertical, EyeSlash, ClockClockwise } from "@phosphor-icons/react";

// Alert titles that FollowUpBoard now owns end-to-end. NBA drops these
// two before ranking so the same lead doesn't show up in both surfaces.
//
// TODO: expose alert type/id in next-best-action payload and remove
// title-based filtering. The current payload — both AI (llm_service.py
// `next_best_action`) and rule-based (alerts_engine.py `fallback_nba`)
// — only exposes {priority, title, reason, cta, cta_tab, severity,
// entity_type, entity_id}. `type` and `id` from the source alert never
// survive the mapping, so we can't do a stable slug match without
// touching the backend. Same for `entity_type`: follow-up, needs_response
// and stale all share `entity_type: "lead"`, so gating by it would drop
// legacy lead alerts we must keep. i18n strings live in
// backend/alerts_engine.py (`lead_followup_*` in the ES/EN dicts).
const FOLLOWUP_ALERT_TITLE_PREFIXES = [
  "Seguimiento vencido ·",
  "Seguimiento hoy ·",
  "Follow-up overdue ·",
  "Follow-up today ·",
];

function isFollowUpAlert(a) {
  const title = (a && a.title) || "";
  return FOLLOWUP_ALERT_TITLE_PREFIXES.some((p) => title.startsWith(p));
}

const SEV_COLOR = {
  high: { bg: "rgba(156,82,54,0.08)", border: "#9C5236", dot: "#C1432A" },
  medium: { bg: "rgba(194,143,60,0.08)", border: "#C28F3C", dot: "#C28F3C" },
  low: { bg: "rgba(47,62,53,0.06)", border: "#2F3E35", dot: "#2F3E35" },
};

const I18N = {
  es: {
    nbaEyebrow: "Next Best Action · IA",
    nbaTitle: "Hoy deberías…",
    refreshTitle: "Recalcular con IA",
    analyzing: "Analizando con IA…",
    allGood: "Todo en orden. Disfruta el día.",
    priority: "Prioridad",
    sevLabel: { high: "ALTA", medium: "MEDIA", low: "BAJA" },
    source: { ai: "⚡ Priorizado por Claude", rules: "Ordenado por reglas (IA no disponible)" },
    cta: "Ir",
    loadingAlerts: "Cargando alertas…",
    alertsTitle: "Centro de alertas",
    activeAlerts: (n) => `${n} alerta(s) activas`,
    highN: (n) => `${n} altas`,
    mediumN: (n) => `${n} medias`,
    lowN: (n) => `${n} bajas`,
    reload: "Recargar",
    allClear: "Todo en orden",
    allClearSub: "No hay alertas pendientes ahora mismo.",
    sevGroup: { high: "Urgentes", medium: "Importantes", low: "Seguimiento" },
    daysAgo: (n) => `${n} día(s)`,
    dismissMenu: "Más opciones",
    dismissHide: "Ocultar",
    dismissSnooze7: "Posponer 7 días",
    dismissedHidden: "Alerta ocultada",
    dismissedSnoozed: "Alerta pospuesta 7 días",
    dismissUndo: "Deshacer",
  },
  en: {
    nbaEyebrow: "Next Best Action · AI",
    nbaTitle: "Today you should…",
    refreshTitle: "Recompute with AI",
    analyzing: "Analyzing with AI…",
    allGood: "All clear. Enjoy the day.",
    priority: "Priority",
    sevLabel: { high: "HIGH", medium: "MEDIUM", low: "LOW" },
    source: { ai: "⚡ Prioritized by Claude", rules: "Rule-based ordering (AI unavailable)" },
    cta: "Go",
    loadingAlerts: "Loading alerts…",
    alertsTitle: "Alerts center",
    activeAlerts: (n) => `${n} active alert(s)`,
    highN: (n) => `${n} high`,
    mediumN: (n) => `${n} medium`,
    lowN: (n) => `${n} low`,
    reload: "Reload",
    allClear: "All clear",
    allClearSub: "No pending alerts right now.",
    sevGroup: { high: "Urgent", medium: "Important", low: "Follow-up" },
    daysAgo: (n) => `${n} day(s)`,
    dismissMenu: "More options",
    dismissHide: "Hide",
    dismissSnooze7: "Snooze 7 days",
    dismissedHidden: "Alert hidden",
    dismissedSnoozed: "Alert snoozed 7 days",
    dismissUndo: "Undo",
  },
};

/** The daily "Next Best Action" widget — meant to sit on top of an overview page. */
export default function NextBestAction({ accent = "#1C1C1E", onNavigate, lang = "es" }) {
  const t = I18N[lang] || I18N.es;
  const [brief, setBrief] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/alerts/next-best-action?lang=${lang}`);
      setBrief(data);
    } catch (e) {
      setErr(formatError(e));
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const nav = (tab) => { if (tab && onNavigate) onNavigate(tab); };

  // Drop follow-up-overdue / follow-up-due-today — the FollowUpBoard is now
  // their canonical surface. Legacy `lead.stale` and `lead.needs_response`
  // stay because they cover the pre-follow-up cadence (no next_action_at).
  const filteredActions = useMemo(
    () => (brief?.actions || []).filter((a) => !isFollowUpAlert(a)),
    [brief],
  );

  return (
    <div className="rounded-xl overflow-hidden mb-10 fade-up border border-stone-200 bg-white"
      style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
      {/* Header */}
      <div className="px-6 py-5 flex items-center justify-between border-b border-stone-100"
        style={{ background: `linear-gradient(90deg, ${accent}0D, transparent 60%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ background: accent }}>
            <Sparkle size={18} weight="fill" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500">{t.nbaEyebrow}</div>
            <div className="font-serif-dv text-2xl leading-tight">{t.nbaTitle}</div>
          </div>
        </div>
        <button
          onClick={load}
          disabled={busy}
          data-testid="nba-refresh"
          title={t.refreshTitle}
          className="dv-btn dv-btn-ghost !p-2"
        >
          <ArrowClockwise size={18} className={busy ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Body */}
      <div className="p-6">
        {busy && !brief && (
          <div className="text-stone-400 text-sm">{t.analyzing}</div>
        )}
        {err && !brief && (
          <div className="text-red-600 text-sm">{err}</div>
        )}
        {brief && filteredActions.length === 0 && (
          <div className="flex items-center gap-3 text-stone-600">
            <CheckCircle size={20} weight="fill" className="text-emerald-600" />
            <span>{brief.greeting || t.allGood}</span>
          </div>
        )}
        {brief && filteredActions.length > 0 && (
          <>
            {brief.greeting && (
              <p className="text-stone-600 italic mb-5">"{brief.greeting}"</p>
            )}
            <div className="space-y-3">
              {filteredActions.slice(0, 5).map((a, i) => {
                const sev = SEV_COLOR[a.severity] || SEV_COLOR.medium;
                const sevLbl = t.sevLabel[a.severity] || t.sevLabel.medium;
                return (
                  <div
                    key={i}
                    data-testid={`nba-action-${i+1}`}
                    className="flex items-start gap-4 p-4 rounded-lg border transition-transform hover:-translate-y-0.5"
                    style={{ background: sev.bg, borderColor: sev.border + "40" }}
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center font-serif-dv text-lg shrink-0 text-white" style={{ background: sev.dot }}>
                      {a.priority || i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-stone-900">{a.title}</div>
                      <div className="text-sm text-stone-600 mt-1">{a.reason}</div>
                      <div className="text-[10px] tracking-widest uppercase text-stone-400 mt-2">
                        {t.priority} {sevLbl}
                      </div>
                    </div>
                    {a.cta_tab && onNavigate && (
                      <button
                        onClick={() => nav(a.cta_tab)}
                        className="dv-btn dv-btn-ghost !px-3 !py-2 text-sm whitespace-nowrap shrink-0"
                      >
                        {a.cta || t.cta} <ArrowRight size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 text-[10px] text-stone-400 tracking-wider uppercase">
              {brief.source === "ai" ? t.source.ai : brief.source === "rules" ? t.source.rules : ""}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Full alerts list view for dedicated tab */
export function AlertsBoard({ onNavigate, lang = "es" }) {
  const t = I18N[lang] || I18N.es;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  // Open kebab menu (alert.id) and last dismissal feedback for "Undo".
  const [openMenu, setOpenMenu] = useState(null);
  const [lastDismiss, setLastDismiss] = useState(null); // {alertId, label}

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get(`/alerts?lang=${lang}`);
      setData(data);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const dismiss = async (alert, snoozeDays) => {
    setOpenMenu(null);
    const body = snoozeDays ? { snooze_days: snoozeDays } : {};
    try {
      await api.post(`/alerts/${encodeURIComponent(alert.id)}/dismiss`, body);
      setLastDismiss({
        alertId: alert.id,
        label: snoozeDays ? t.dismissedSnoozed : t.dismissedHidden,
      });
      load();
    } catch (e) {
      setErr(formatError(e));
    }
  };

  const undoDismiss = async () => {
    if (!lastDismiss) return;
    const alertId = lastDismiss.alertId;
    setLastDismiss(null);
    try {
      await api.delete(`/alerts/${encodeURIComponent(alertId)}/dismiss`);
      load();
    } catch (e) { setErr(formatError(e)); }
  };

  if (busy && !data) return <div className="text-stone-400">{t.loadingAlerts}</div>;
  if (err) return <div className="text-red-700 dv-card">{err}</div>;
  if (!data) return null;

  const groups = { high: [], medium: [], low: [] };
  data.alerts.forEach(a => groups[a.severity]?.push(a));

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-serif-dv text-3xl">{t.alertsTitle}</h2>
          <p className="text-stone-500 mt-1">
            {t.activeAlerts(data.total)} · <span className="text-red-700">{t.highN(data.by_severity.high)}</span> · <span className="text-amber-700">{t.mediumN(data.by_severity.medium)}</span> · <span className="text-emerald-700">{t.lowN(data.by_severity.low)}</span>
          </p>
        </div>
        <button onClick={load} data-testid="alerts-refresh" className="dv-btn dv-btn-outline">
          <ArrowClockwise size={16} /> {t.reload}
        </button>
      </div>

      {lastDismiss && (
        <div
          className="mb-4 flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-stone-200 bg-stone-50 text-sm text-stone-700"
          data-testid="alerts-dismiss-toast"
        >
          <span>{lastDismiss.label}</span>
          <button
            onClick={undoDismiss}
            className="text-stone-900 underline hover:no-underline"
            data-testid="alerts-dismiss-undo"
          >
            {t.dismissUndo}
          </button>
        </div>
      )}

      {data.alerts.length === 0 && (
        <div className="dv-card text-center py-16">
          <CheckCircle size={48} weight="light" className="mx-auto text-emerald-500 mb-3" />
          <div className="font-serif-dv text-2xl">{t.allClear}</div>
          <div className="text-stone-500 mt-1">{t.allClearSub}</div>
        </div>
      )}

      {["high", "medium", "low"].map(sev => groups[sev].length > 0 && (
        <div key={sev} className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: SEV_COLOR[sev].dot }} />
            <div className="text-xs uppercase tracking-[0.25em] text-stone-600">
              {t.sevGroup[sev]} · {groups[sev].length}
            </div>
          </div>
          <div className="space-y-3">
            {groups[sev].map(a => (
              <div key={a.id} data-testid={`alert-${a.id}`}
                className="dv-card flex items-start gap-4 hover:-translate-y-0.5 transition-transform relative"
                style={{ borderLeft: `3px solid ${SEV_COLOR[sev].border}` }}>
                <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ background: SEV_COLOR[sev].bg, color: SEV_COLOR[sev].dot }}>
                  {sev === "high" ? <Warning size={20} weight="fill" /> : <Clock size={20} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{a.title}</div>
                  <div className="text-sm text-stone-600 mt-1">{a.description}</div>
                  {a.days_old > 0 && a.days_old < 900 && (
                    <div className="text-xs text-stone-400 mt-1">{t.daysAgo(a.days_old)}</div>
                  )}
                </div>
                {a.cta_tab && onNavigate && (
                  <button onClick={() => onNavigate(a.cta_tab)} className="dv-btn dv-btn-ghost text-sm !px-3 !py-2 shrink-0">
                    {a.cta} <ArrowRight size={14} />
                  </button>
                )}
                <div className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => setOpenMenu(openMenu === a.id ? null : a.id)}
                    className="p-1.5 rounded hover:bg-stone-100 text-stone-500"
                    title={t.dismissMenu}
                    aria-haspopup="menu"
                    aria-expanded={openMenu === a.id}
                    data-testid={`alert-menu-${a.id}`}
                  >
                    <DotsThreeVertical size={18} weight="bold" />
                  </button>
                  {openMenu === a.id && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setOpenMenu(null)}
                        aria-hidden="true"
                      />
                      <div
                        role="menu"
                        className="absolute right-0 top-full mt-1 z-20 min-w-[180px] rounded-md border border-stone-200 bg-white shadow-lg py-1 text-sm"
                        data-testid={`alert-menu-open-${a.id}`}
                      >
                        <button
                          type="button"
                          onClick={() => dismiss(a, null)}
                          className="w-full px-3 py-2 text-left hover:bg-stone-50 inline-flex items-center gap-2"
                          data-testid={`alert-dismiss-hide-${a.id}`}
                        >
                          <EyeSlash size={14} /> {t.dismissHide}
                        </button>
                        <button
                          type="button"
                          onClick={() => dismiss(a, 7)}
                          className="w-full px-3 py-2 text-left hover:bg-stone-50 inline-flex items-center gap-2"
                          data-testid={`alert-dismiss-snooze7-${a.id}`}
                        >
                          <ClockClockwise size={14} /> {t.dismissSnooze7}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
